import React, { useCallback, useState } from 'react';
import { v1 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import { useDropzone } from 'react-dropzone';
import { LookupBase as LookupField } from 'fields/Lookup';
import pageActions from 'core/page/actions';
import Button from 'common/Button';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';

import iconPaperClip from 'icons/paper-clip.svg';
import buttonLinkStyles from 'styles/buttonLink';
import visuallyHidden from 'styles/visuallyHidden';
import dialogStyles from './styles/dialog';
import contentStyles from './styles/content';
import contentWrapperStyles from './styles/contentWrapper';
import bodyStyles from './styles/body';
import headerStyles from './styles/header';
import footerStyles from './styles/footer';
import titleStyles from './styles/title';

const styles = (theme) => {
  const common = {
    body: bodyStyles(theme),
    content: {
      ...contentStyles(theme),
      height: 'auto',
      minHeight: '100%',
    },
    contentWrapper: contentWrapperStyles(theme),
    dialog: dialogStyles(theme),
    footer: footerStyles(theme),
    header: headerStyles(theme),
    title: titleStyles(theme),
  };
  return {
    ...common,
    button: {
      ...buttonLinkStyles(theme),
    },
    dialog: {
      ...common.dialog,
      textAlign: 'center',
    },
    dndSupport: {
      background: `url(${iconPaperClip}) 0 1px no-repeat`,
      backgroundSize: '14px 16px',
      padding: [0, 0, 0, 25],
    },
    dndSupportDrag: {
      alignItems: 'center',
      background: theme.palette.icon,
      color: theme.links.link.color,
      display: 'none',
      fontSize: 36,
      height: '100%',
      justifyContent: 'center',
      left: 0,
      position: 'absolute',
      top: 0,
      width: '100%',
    },
    documentType: {
      marginBottom: theme.spacing.sm,
    },
    dragging: {
      '& $dndSupportDrag': {
        display: 'flex',
      },
    },
    dropzone: {
      padding: theme.spacing.md,
      position: 'relative',
    },
    errorMsg: {},
    fileUpload: {
      border: `1px dashed ${theme.palette.form.border}`,
      cursor: 'pointer',
    },
    fileUploadContent: {
      paddingBottom: 15,
    },
    hidden: {
      display: 'none',
    },
    maxSize: {
      display: 'block',
      fontSize: 12,
      width: '100%',
    },
    remove: {
      '&:hover': {
        color: theme.links.link.hoverColor,
        textDecoration: 'underline',
      },
      background: 'none',
      border: 'none',
      color: theme.links.link.color,
      cursor: 'pointer',
      fontSize: 14,
      position: 'absolute',
      right: 5,
      top: 18,
    },
    uploadedFile: {
      '& .spinner': {
        position: 'absolute',
        right: 10,
        top: 25,
      },
      '& span': {
        display: 'block',
        fontSize: 14,
        width: '100%',
      },
      '& strong': {
        color: theme.palette.form.placeholder,
        display: 'block',
        width: '100%',
      },
      boxShadow: theme.shadows.tooltip,
      display: 'block',
      marginBottom: theme.spacing.xs,
      overflow: 'hidden',
      padding: [15, 75, 15, 15],
      position: 'relative',
      textAlign: 'left',
      textOverflow: 'ellipsis',
      width: '100%',
    },
    visuallyHidden,
    workspace: {
      marginTop: theme.spacing.xs,
    },
  };
};

const HomeDocumentUpload = ({
  appRequest,
  categories,
  category,
  categoryLabel,
  classes,
  close,
  commonClasses,
  docUploadPopupSubtitle,
  docUploadPopupTitle,
  dragOrSelectFilesLabel,
  fileUploadTypes,
  goToPageError,
  maxFileSizeLabel,
  modalCancelLabel,
  modalRemoveLabel,
  modalSubmitLabel,
  onClickNext,
  uploadAndSubmitLabel,
  uploadedFiles,
  url,
}) => {
  const [categoryValue, setCategoryValue] = useState(category);
  const [categoryTitle, setCategoryTitle] = useState(categoryLabel);
  const [files, setFiles] = useState(uploadedFiles);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  function isIOSCameraImage(file) {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return userAgent.match(/iPhone/i);
  }
  const onDrop = useCallback(
    (acceptedFiles) => {
      setErrorMsg('');
      acceptedFiles.forEach((file) => {
        const fileType = file.type.split('/')[1];
        if (fileUploadTypes.includes(fileType)) {
          const fileNotUploaded = files.filter(
            (f) => f.name === file.name && f.documentType === file.documentType,
          );
          if (fileNotUploaded.length === 0) {
            setLoading(true);

            let newName;

            if (isIOSCameraImage(file)) {
              const timestamp = new Date().getTime();
              newName = `${timestamp}_${file.name}`;
            } else {
              newName = file.name;
            }
            const newFile = {
              ...file,
              documentType: categoryValue,
              documentTypeLabel: categoryTitle,
              id: uuid(),
              loading: true,
              name: newName,
            };
            // add file
            setFiles((fs) => fs.concat(newFile));
            // upload file
            const formData = new FormData();
            formData.append('file', file, newName);
            const postUrl = `${url}${appRequest ? '/upload/' : '/categories/'}${categoryValue}`;
            axios({
              data: formData,
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              method: 'POST',
              url: postUrl,
            })
              .then(() => {
                setFiles((f) =>
                  f.map((curr) => {
                    if (curr.name === newFile.name && curr.documentType === newFile.documentType) {
                      return {
                        ...curr,
                        loading: false,
                      };
                    }
                    return curr;
                  }),
                );
                setLoading(false);
              })
              .catch((error) => {
                setFiles((f) => f.filter((curr) => curr.id !== newFile.id));
                const errorRs =
                  error?.response?.data?.result?.msgs?.['*'] ??
                  error?.response?.data?.msg ??
                  error.message;
                setErrorMsg(errorRs);
                setLoading(false);
              });
          }
        } else {
          setErrorMsg(`Please select a valid file type: ${fileUploadTypes.join(', ')}`);
        }
      });
    },
    [appRequest, categoryTitle, categoryValue, fileUploadTypes, files, url],
  );

  const handleRemoveFile = useCallback(
    ({ documentType, id, name }) => {
      setFiles((f) =>
        f.map((file) => {
          if (id === file.id) {
            return {
              ...file,
              loading: true,
            };
          }
          return file;
        }),
      );

      const deleteUrl = `${url}${appRequest ? '/' : '/categories/'}${documentType}/${name}`;
      axios({
        method: 'DELETE',
        url: deleteUrl,
      })
        .then(() => {
          const filteredFiles = files.filter((file) => file.id !== id);
          setFiles(filteredFiles);
        })
        .catch((resp) => {
          goToPageError(resp.result, resp.status);
        });
    },
    [appRequest, files, goToPageError, url],
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({ onDrop });

  const lookupFieldProps = {
    error: false,
    errorMsg: '',
    fieldDescr: '',
    fieldDomId: 'DOCUMENT_UPLOAD_GROUP-document_upload_category_lookup',
    focus: true,
    groupName: 'DOCUMENT_UPLOAD_GROUP',
    help: '',
    id: 0,
    label: '',
    loading: false,
    name: 'document_upload_category_lookup',
    onBlur: () => {},
    onChange: (clientId, val) => {
      setCategoryValue(val);
      setCategoryTitle(categories[val]);
    },
    onFocus: () => {},
    placeholder: 'Select Document Type',
    reRender: () => {},
    readOnly: false,
    required: true,
    tooltip: '',
    type: 'text',
    validate: () => {},
    value: categoryValue,
  };

  const buttons = [
    {
      className: 'btn btn-mobile btn-uploader-next',
      onClick: () => onClickNext(`${url}${appRequest ? '/submit' : '/categories'}`),
      text: modalSubmitLabel,
      variant: 'primary',
    },
    {
      className: 'btn btn-mobile btn-mobile--secondary',
      onClick: close,
      text: modalCancelLabel,
      variant: 'secondary',
    },
  ];

  return (
    <div className={classnames('modal-pdf', commonClasses, classes.dialog)}>
      <div className={classnames('modal-content', classes.content)}>
        <div className={classnames('modal-header', classes.header)}>
          <h4 className={classnames('modal-title', classes.title)} id="modal-title">
            {docUploadPopupTitle}
          </h4>
        </div>
        <div className={classnames('modal-body', classes.body)} id="modal-body">
          <div className={classnames('modal-content-wrapper', classes.contentWrapper)}>
            <div className={classnames('file-upload-content', classes.fileUploadContent)}>
              <RawHtml type="p">{docUploadPopupSubtitle}</RawHtml>
              <form>
                <div className={classnames('region-document-type', classes.documentType)}>
                  {categories && <LookupField {...lookupFieldProps} options={categories} />}
                </div>
                <div
                  className={classnames('file-upload', classes.fileUpload, {
                    active: categoryTitle,
                    hidden: !categoryValue,
                  })}
                >
                  <div
                    {...getRootProps()}
                    className={classnames('dropzone clickable', classes.dropzone, {
                      dragging: isDragActive,
                      [classes.dragging]: isDragActive,
                    })}
                  >
                    <span className={classnames('dnd-support', classes.dndSupport)}>
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <button
                        className={classnames('buttonLink', classes.button)}
                        type="button"
                        onClick={(e) => e.preventDefault()}
                      >
                        {dragOrSelectFilesLabel}
                      </button>
                    </span>
                    <span className={classnames('dnd-support-drag', classes.dndSupportDrag)}>
                      Drop files here.
                    </span>
                    <small className={classes.maxSize}>{maxFileSizeLabel}</small>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="a11y-dropzone" />
                    <input
                      {...getInputProps()}
                      id="a11y-dropzone"
                      type="file"
                      name="file"
                      aria-describedby={classnames({
                        'error-home-document-upload': !!errorMsg,
                      })}
                    />
                  </div>
                  <div className="status clearfix hide">
                    <div className="status-cell">
                      <div className="loader hide" />
                      <span className="glyphicon status-icon icon-white" />
                      <span className="message" />
                    </div>
                  </div>
                </div>

                <ErrorMsg error={!!errorMsg} errorMsg={errorMsg} name="home-document-upload" />

                <div className={classnames('workspace', classes.workspace)}>
                  {files.map((file) => (
                    <div
                      key={file.name}
                      className={classnames('uploaded-file', classes.uploadedFile, {
                        loading: file.loading,
                      })}
                      data-name={file.name}
                      data-type={file.documentType}
                    >
                      <RawHtml type="strong">{file.documentTypeLabel}</RawHtml>
                      <RawHtml type="span">{file.name}</RawHtml>
                      {file.loading ? (
                        <Spinner className={classes.spinner} loading={file.loading} />
                      ) : (
                        <button
                          type="button"
                          disabled={loading}
                          className={classnames('icon-close-solid btn-remove', classes.remove)}
                          onClick={() => handleRemoveFile(file)}
                        >
                          {modalRemoveLabel}
                        </button>
                      )}
                      <p role="alert" className={classnames('sr-only', classes.visuallyHidden)}>
                        {!file.loading ? `File "${file.name}" has uploaded successfully` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className={classnames('modal-footer', classes.footer)}>
          <p className={classes.uploadAndSubmitLabel}>{uploadAndSubmitLabel}</p>
          {buttons.map((b, i) => (
            <Button
              key={`dialog-button-${b.text}`}
              className={b.className}
              data-key={i}
              disabled={b.variant === 'primary' ? loading || files.length === 0 : false}
              variant={b.variant}
              onClick={b.onClick}
            >
              {b.text}
              {b.variant === 'primary' && (
                <>
                  <i className="icon-arrow-thin-right" aria-hidden="true" />
                  <Spinner loading={false} />
                </>
              )}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

HomeDocumentUpload.propTypes = {
  appRequest: PropTypes.shape({}),
  categories: PropTypes.shape({}),
  category: PropTypes.string,
  categoryLabel: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  close: PropTypes.func.isRequired,
  commonClasses: PropTypes.string.isRequired,
  docUploadPopupSubtitle: PropTypes.string,
  docUploadPopupTitle: PropTypes.string,
  dragOrSelectFilesLabel: PropTypes.string,
  fileUploadTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  goToPageError: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  maxFileSizeLabel: PropTypes.string,
  modalCancelLabel: PropTypes.string,
  modalRemoveLabel: PropTypes.string,
  modalSubmitLabel: PropTypes.string,
  onClickNext: PropTypes.func.isRequired,
  uploadAndSubmitLabel: PropTypes.string,
  uploadedFiles: PropTypes.arrayOf(PropTypes.shape({})),
  url: PropTypes.string.isRequired,
};

HomeDocumentUpload.defaultProps = {
  appRequest: null,
  categories: null,
  category: '',
  categoryLabel: '',
  docUploadPopupSubtitle: '',
  docUploadPopupTitle: 'Document Upload',
  dragOrSelectFilesLabel: 'Drag files here or select files from your computer',
  loading: false,
  maxFileSizeLabel: '10MB individual file maximum.',
  modalCancelLabel: 'Cancel',
  modalRemoveLabel: 'Remove',
  modalSubmitLabel: 'Submit',
  uploadAndSubmitLabel: 'Upload one or more documents and click SUBMIT when finished.',
  uploadedFiles: [],
};

const mapStateToProps = ({ configs, dialogs, page, resources }) => ({
  ...dialogs.params,
  disclosureCancelLabel: resources.disclosureCancelLabel,
  docUploadPopupSubtitle: resources.docUploadPopupSubtitle,
  docUploadPopupTitle: resources.docUploadPopupTitle,
  dragOrSelectFilesLabel: resources.dragOrSelectFilesLabel,
  fileUploadTypes: configs.fileUploadTypes.split(','),
  maxFileSizeLabel: resources.maxFileSizeLabel,
  modalCancelLabel: resources.modalCancelLabel,
  modalRemoveLabel: resources.modalRemoveLabel,
  modalSubmitLabel: resources.modalSubmitLabel,
  pageName: page.name,
  uploadAndSubmitLabel: resources.uploadAndSubmitLabel,
});

const mapDispatchToProps = {
  close: pageActions.getCurrent,
  goToPageError: pageActions.goToPageError,
  onClickNext: pageActions.documentUploadNext,
};

const HomeDocumentUploadStyled = withStyles(styles)(HomeDocumentUpload);

export default connect(mapStateToProps, mapDispatchToProps)(HomeDocumentUploadStyled);
